import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

const LANGUAGES = [
  { code: 'en', name: 'English', flag: 'https://flagcdn.com/w40/us.png' },
  { code: 'zh', name: '中文', flag: 'https://flagcdn.com/w40/cn.png' },
  { code: 'ko', name: '한국어', flag: 'https://flagcdn.com/w40/kr.png' },
  { code: 'ja', name: '日本語', flag: 'https://flagcdn.com/w40/jp.png' }
];

type SubpageNavProps = {
  /** i18n key for back button label, default 'common.backToHome' */
  backToHomeKey?: string;
  /** Optional subtitle under CTL logo (e.g. company name) */
  logoSubtitle?: string;
};

export default function SubpageNav({ backToHomeKey = 'common.backToHome', logoSubtitle }: SubpageNavProps) {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [showLangMenu, setShowLangMenu] = useState(false);

  const currentLanguage = LANGUAGES.find((lang) => lang.code === i18n.language) || LANGUAGES[0];

  const changeLanguage = (langCode: string) => {
    i18n.changeLanguage(langCode);
    setShowLangMenu(false);
  };

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-sm shadow-sm">
      <div className="max-w-7xl mx-auto px-6">
        <div className="flex items-center justify-between h-20">
          <div
            className="flex items-center gap-3 cursor-pointer"
            onClick={() => navigate('/')}
          >
            <div className="w-12 h-12 bg-gradient-to-br from-teal-500 to-teal-600 rounded-xl flex items-center justify-center shadow-lg">
              <i className="ri-test-tube-line text-2xl text-white"></i>
            </div>
            <div>
              <h1 className="text-xl font-bold text-gray-900">CTL</h1>
              {logoSubtitle && (
                <p className="text-xs text-gray-600">{logoSubtitle}</p>
              )}
            </div>
          </div>

          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate(-1)}
              className="flex items-center gap-2 px-6 py-2 bg-teal-600 text-white rounded-lg font-semibold hover:bg-teal-700 transition-all whitespace-nowrap cursor-pointer"
            >
              <i className="ri-arrow-left-line text-lg"></i>
              <span>{t(backToHomeKey)}</span>
            </button>

            <div className="relative">
              <button
                onClick={() => setShowLangMenu(!showLangMenu)}
                onBlur={() => setTimeout(() => setShowLangMenu(false), 200)}
                className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-100 transition-all cursor-pointer border border-gray-200"
              >
                <img
                  src={currentLanguage.flag}
                  alt={currentLanguage.name}
                  className="w-5 h-4 object-cover rounded"
                />
                <span>{currentLanguage.code.toUpperCase()}</span>
                <i className={`ri-arrow-down-s-line transition-transform ${showLangMenu ? 'rotate-180' : ''}`}></i>
              </button>
              {showLangMenu && (
                <div className="absolute top-full right-0 mt-1 w-40 bg-white rounded-lg shadow-lg py-2 z-50 border border-gray-200">
                  {LANGUAGES.map((lang) => (
                    <button
                      key={lang.code}
                      onClick={() => changeLanguage(lang.code)}
                      className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 cursor-pointer flex items-center gap-2"
                    >
                      <img
                        src={lang.flag}
                        alt={lang.name}
                        className="w-5 h-4 object-cover rounded"
                      />
                      <span>{lang.name}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </nav>
  );
}
